// src/services/classRelease.js
// ── Release-day classes (docs/plans/release-day-classes-plan.md) ──
//
// A pre-recorded class can be scheduled by DAY instead of by time:
//   liveClasses.releaseMode === 'day'
//   liveClasses.releaseDate  === 'YYYY-MM-DD'
//   liveClasses.scheduledAt  === `${releaseDate}T12:00:00.000Z`  (the "anchor")
//
// Every member gets the class at 12:00 AM on releaseDate in HER OWN time zone.
// The anchor (noon UTC on the release date) is the instant at which that date
// has begun everywhere on Earth — the server cron uses it to file the class into
// On Demand. Keeping scheduledAt populated means every existing query that
// orders or ranges on it (30-day fetch window, cron window, Insights) still works.
//
// Everything in this file is READ-ONLY and per-viewer: nothing here writes a
// class's state (owner rule 2026-10-06 — members' devices never write class
// state). A release-day class never goes 'live'; the cron moves it straight from
// 'scheduled' to 'ended' at the anchor.
//
// A class WITHOUT releaseMode === 'day' is untouched by every function below,
// so all existing classes (and every true-live class) behave exactly as before.

import { serverNow } from './serverClock';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isReleaseDayClass(cls) {
    return !!cls && cls.releaseMode === 'day' && typeof cls.releaseDate === 'string' && DATE_RE.test(cls.releaseDate);
}

// The stored anchor for a release date (see header).
export function releaseAnchorIso(releaseDate) {
    return `${releaseDate}T12:00:00.000Z`;
}

// Local calendar key (YYYY-MM-DD) for a Date in THIS device's time zone.
export function localDateKey(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// 12:00 AM on the release date in THIS device's time zone, as a Date.
export function localReleaseStart(releaseDate) {
    const [y, m, d] = releaseDate.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 0, 0, 0, 0);
}

// Has this class opened for this viewer yet? Uses the server-anchored clock so a
// wrong device clock can't open it early (a changed device TIME ZONE still can —
// accepted by the owner 2026-10-13; the video id is already readable pre-release).
export function isReleasedForViewer(cls, nowMs = serverNow()) {
    if (!isReleaseDayClass(cls)) return false;
    return nowMs >= localReleaseStart(cls.releaseDate).getTime();
}

// Monday of the viewer's current week as a YYYY-MM-DD key (the Schedule's
// Monday→Sunday week, same rule as isAiredThisWeek in Workouts.js).
export function localWeekStartKey(now = new Date()) {
    const daysSinceMonday = (now.getDay() + 6) % 7;
    return localDateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday));
}

// The viewer's view of a release-day class. Returns the class unchanged when it
// is not a release-day class. Otherwise returns a COPY whose `scheduledAt` is
// the viewer's local 12:00 AM on the release date (so every existing day-group /
// week / ribbon / sort helper places it on the right day with no further
// changes), plus a `_release` descriptor the Schedule UI reads. The original
// anchor is kept on `_release.anchorAt`. Never persisted — display only.
export function projectReleaseClass(cls, now = new Date(), nowMs = serverNow()) {
    if (!isReleaseDayClass(cls)) return cls;
    const start = localReleaseStart(cls.releaseDate);
    const todayKey = localDateKey(now);
    const released = nowMs >= start.getTime();
    return {
        ...cls,
        scheduledAt: start.toISOString(),
        _release: {
            date: cls.releaseDate,
            anchorAt: cls.scheduledAt,
            released,
            isToday: cls.releaseDate === todayKey,
            isPastDay: cls.releaseDate < todayKey,
            weekday: start.toLocaleDateString('en-US', { weekday: 'long' }),
        },
    };
}

// The player object for a released class. Prefers the real On Demand recording
// (exists once the cron has filed it — and always for a RE-AIR), and otherwise
// builds the same shape from the class doc so the class can be played from the
// Schedule between a member's midnight and the anchor. Both shapes key on the
// Mux asset id, so completions, watch progress, hearts (by classId) and comments
// land exactly where the recording's will. `preRelease: true` hides the Share
// link (the public class page only knows filed recordings).
export function resolveReleaseVideo(cls, classVideos = []) {
    if (!cls) return null;
    const rec = classVideos.find((v) => v.classId === cls.id)
        || (cls.vodAssetId ? classVideos.find((v) => v.id === cls.vodAssetId) : null)
        || (cls.muxAssetId ? classVideos.find((v) => v.id === cls.muxAssetId) : null);
    if (rec) return rec;
    if (!cls.muxAssetId || !cls.muxPlaybackId) return null;
    const seconds = (typeof cls.videoDurationSeconds === 'number' && cls.videoDurationSeconds > 30)
        ? cls.videoDurationSeconds : (cls.duration || 0) * 60;
    const minutes = seconds ? Math.round(seconds / 60) : (cls.duration || 0);
    return {
        id: cls.muxAssetId,
        muxAssetId: cls.muxAssetId,
        muxPlaybackId: cls.muxPlaybackId,
        title: cls.title || '',
        description: cls.description || '',
        duration: minutes,
        durationSeconds: seconds,
        length: minutes,
        thumbnailUrl: cls.thumbnailUrl || cls.imageUrl || null,
        driveThumbnail: cls.thumbnailUrl || cls.imageUrl || null,
        hlsUrl: `https://stream.mux.com/${cls.muxPlaybackId}.m3u8`,
        classId: cls.id,
        isClassRecording: true,
        isFree: cls.price === 0 || cls.price === '0',
        groups: cls.muscleGroups || [],
        easeIn: !!cls.easeIn,
        strongPlus: !!cls.strongPlus,
        likeCount: cls.likeCount || 0,
        commentCount: cls.commentCount || 0,
        streamUrl: null,
        preRelease: true,
    };
}

// "Wednesday, October 21" — the release date read as a calendar date (no time
// zone conversion: Wednesday is Wednesday for everyone).
export function formatReleaseDate(releaseDate, opts = { weekday: 'long', month: 'long', day: 'numeric' }) {
    if (!releaseDate || !DATE_RE.test(releaseDate)) return '';
    return localReleaseStart(releaseDate).toLocaleDateString('en-US', opts);
}
